// Kort startguide per roll: tre steg i klarspråk första gången rollen öppnar projektet.
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronRight, Compass, X } from "lucide-react";
import type { ProjectRole } from "@/lib/basProcess/roleViews";

interface StartStep {
  title: string;
  text: string;
  link?: string;
}

const GUIDES: Record<ProjectRole, { intro: string; steps: StartStep[] }> = {
  Byggherre: {
    intro:
      "Du har det yttersta arbetsmiljöansvaret. Tre saker gör du först – resten sköter Bas-P och Bas-U åt dig.",
    steps: [
      { title: "Utse Bas-P och Bas-U skriftligt", text: "Utan ett skriftligt utseende ligger hela ansvaret kvar hos dig.", link: "minroll" },
      { title: "Svara på frågorna om projektets storlek och risk", text: "Svaren styr hur mycket dokumentation projektet behöver.", link: "oversikt" },
      { title: "Skicka in förhandsanmälan om projektet är tillräckligt stort", text: "Anmälan görs på Arbetsmiljöverkets webbplats – kvittot laddas upp här.", link: "forhandsanmalan" },
    ],
  },
  Projektör: {
    intro: "Din uppgift är att bygga bort risker – och redovisa de som blir kvar.",
    steps: [
      { title: "Läs arbetsområdesutredningen", text: "Där ser du vad som finns runt omkring och vad som påverkar din projektering.", link: "arbetsomrade" },
      { title: "Lämna in kvarvarande risker för din disciplin", text: "De risker du inte kunnat projektera bort går vidare till Bas-P.", link: "projektorinlamning" },
      { title: "Beskriv riskerna i bruksskedet", text: "Hur ska byggnaden driftas, städas och underhållas säkert?", link: "bruksskede" },
    ],
  },
  "Bas-P": {
    intro: "Du samordnar arbetsmiljön under planering och projektering och äger arbetsmiljöplanen.",
    steps: [
      { title: "Gör arbetsområdesutredningen", text: "Kartlägg platsen: grannar, installationer, trafik och angränsande verksamhet.", link: "arbetsomrade" },
      { title: "Samla riskerna i risksammanställningen", text: "Projektörernas inlämningar och dina egna observationer samlas här.", link: "riskbedomning" },
      { title: "Upprätta arbetsmiljöplanen och lämna över till Bas-U", text: "Planen ska vara klar innan bygget startar.", link: "amp" },
    ],
  },
  "Bas-U": {
    intro: "Du samordnar arbetsmiljön på plats under utförandet.",
    steps: [
      { title: "Ta emot överlämningen från Bas-P", text: "Kvittera arbetsmiljöplanen och de frågor som är kvar att lösa.", link: "overlamning" },
      { title: "Introducera alla på arbetsplatsen", text: "Varje person ska ha gått igenom planen och ordningsreglerna.", link: "introduktion" },
      { title: "Håll skyddsronder och följ upp åtgärder", text: "Det löpande arbetet är det som visar att planen faktiskt följs.", link: "skyddsrond" },
    ],
  },
  Entreprenör: {
    intro: "Du utför arbetet och ska kunna visa att du känner till riskerna och reglerna.",
    steps: [
      { title: "Läs och kvittera arbetsmiljöplanen", text: "Kvittensen visar att du tagit del av planen och ordningsreglerna.", link: "entreprenorer" },
      { title: "Lämna arbetsberedning för riskfyllda moment", text: "Beskriv momentet enkelt – systemet hjälper till med resten.", link: "entreprenorer" },
      { title: "Rapportera tillbud och olyckor direkt", text: "Även det som nästan hände ska rapporteras.", link: "tillbud" },
    ],
  },
  Projektledare: {
    intro:
      "Du har granskande behörighet. Du ser hela processen och alla dokument, men kan inte ändra något.",
    steps: [
      { title: "Se dagsläget", text: "Översikten visar vad som är klart, pågående och försenat i projektet.", link: "dagslage" },
      { title: "Öppna dokumenten och läs", text: "Alla dokument går att öppna och läsa – knappar för att ändra är låsta.", link: "oversikt" },
      { title: "Skriv i meddelanden", text: "Har du synpunkter i granskningen lämnar du dem i projektets meddelanden.", link: "meddelanden" },
    ],
  },
  Uppdragsledare: {
    intro:
      "Du har granskande behörighet. Du följer upp uppdraget och kvaliteten utan att kunna ändra i dokumenten.",
    steps: [
      { title: "Följ upp status", text: "Se hur stor del av processen som är genomförd och vad som saknas.", link: "dagslage" },
      { title: "Granska dokumentationen", text: "Läs igenom arbetsmiljöplanen, riskerna och uppföljningen.", link: "oversikt" },
      { title: "Dokumentera din granskning", text: "Lämna kommentarer i projektets meddelanden.", link: "meddelanden" },
    ],
  },
  "Förvaltare": {
    intro:
      "Du tar emot dokumentationen när bygget är klart och ansvarar för arbetsmiljön i bruksskedet. Du läser och följer upp, men ändrar inget i byggskedet.",
    steps: [
      { title: "Ta emot överlämningen", text: "Kontrollera vad som lämnats över och vad som eventuellt saknas.", link: "overlamning" },
      { title: "Läs kvarvarande risker", text: "Gå igenom riskerna för bruksskedet som du ska förvalta.", link: "bruksskede" },
      { title: "Följ upp i drift", text: "Bevaka åtgärderna som ska göras under förvaltningen.", link: "bruksuppfoljning" },
    ],
  },
};

interface Props {
  role: ProjectRole;
  projectId: string;
  onOpenLink?: (link: string) => void;
}

export const RoleStartGuide: React.FC<Props> = ({ role, projectId, onOpenLink }) => {
  const storageKey = `startguide-dismissed-${projectId}-${role}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(storageKey) === "1");
  const guide = GUIDES[role];
  if (hidden || !guide) return null;

  const dismiss = () => {
    localStorage.setItem(storageKey, "1");
    setHidden(true);
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Compass className="h-4 w-4 text-primary" /> Så här kommer du igång som {role}
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">{guide.intro}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={dismiss} aria-label="Dölj startguiden">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {guide.steps.map((s, i) => (
          <div key={s.title} className="flex flex-wrap items-start gap-3 rounded-md bg-background/70 p-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{s.title}</p>
              <p className="text-sm text-muted-foreground">{s.text}</p>
            </div>
            {s.link && onOpenLink && (
              <Button size="sm" variant="ghost" className="gap-1" onClick={() => onOpenLink(s.link!)}>
                Öppna <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default RoleStartGuide;
